import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import Tab from "@insite/mobius/Tab";
import TabGroup, { TabGroupPresentationProps } from "@insite/mobius/TabGroup";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries, injectCss } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import React from "react";
import { Link } from "react-router-dom";
import { css } from "styled-components";

const enum fields {
    title = "title",
    numberOfTabs = "numberOfTabs",
    cssClass = "cssClass",
}

interface NameArray {
    name: string;
}

interface TabModel {
    fields: NameArray;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.numberOfTabs]: TabModel[];
        [fields.cssClass]: string;
    };
}

type Props = OwnProps;

export interface SurStaticTabStyles {
    tabView?: InjectableCss;
    tabTitle?: InjectableCss;
    tabGroup?: TabGroupPresentationProps;
    container?: InjectableCss;
}
export const surStaticTabStyles: SurStaticTabStyles = {
    tabView: {
        css: css`
            background: ${getColor("common.backgroundContrast")};
            padding: 8px 12px;
            font-style: normal;
            min-width: auto;
            font-weight: ${TYPOGRAPHY.weight.base};
            font-size: ${TYPOGRAPHY.size.small};
            line-height: ${TYPOGRAPHY.lineHeight.msmall};
            transition: none !important;
            color: ${getColor("text.main")};
            cursor: pointer;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 12px;
                        background: ${getColor("secondary")};
                    `,
                    css`
                        padding: 12px;
                        background: ${getColor("secondary")};
                    `,
                    css`
                        padding: 12px;
                        background: ${getColor("secondary")};
                    `,
                    null,
                    null,
                ])}
            &[aria-selected="false"] {
                border-bottom: 2px solid transparent;
            }
            &[aria-selected="true"] {
                span {
                    font-family: "Mulish";
                    font-style: normal;
                    font-weight: 700;
                    font-size: 15px;
                    line-height: 24px;
                    color: ${getColor("text.main")};
                }
                border-bottom: 2px solid #323132;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            span {
                                color: ${getColor("text.main")};
                            }
                            border-bottom: 2px solid #323132;
                        `,
                        css`
                            span {
                                color: ${getColor("text.main")};
                            }
                            border-bottom: 2px solid #323132;
                        `,
                        css`
                            span {
                                color: ${getColor("text.main")};
                            }
                            border-bottom: 2px solid #323132;
                        `,
                        null,
                        null,
                    ])}
            }
        `,
    },
    tabTitle: {
        css: css`
            font-family: "Mulish";
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: 28px;
            line-height: 36px;
            color: ${getColor("text.main")};
            /* identical to box height, or 117% */
            margin-bottom: 24px;
            text-align: center;
            text-transform: uppercase;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        color: ${COLORS.featuredMobile};
                        font-size: ${TYPOGRAPHY.size.largest};
                        line-height: ${TYPOGRAPHY.lineHeight.larger};
                    `,
                    css`
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        color: ${COLORS.featuredMobile};
                        font-size: ${TYPOGRAPHY.size.largest};
                        line-height: ${TYPOGRAPHY.lineHeight.larger};
                    `,
                    css`
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        color: ${COLORS.featuredMobile};
                        font-size: ${TYPOGRAPHY.size.largest};
                        line-height: ${TYPOGRAPHY.lineHeight.larger};
                    `,
                    null,
                    null,
                ])}
        `,
    },
    tabGroup: {
        cssOverrides: {
            tabGroup: css`
                align-items: center;
                justify-content: flex-start;
                gap: 20px;
                z-index: 9;
                margin: 0 auto;
                margin-bottom: 35px;
                padding: 0px;
                &::-webkit-scrollbar{
                    width:0;
                }
                > li > span {
                    font-family: ${TYPOGRAPHY.fontFamily.title};
                    font-weight: ${TYPOGRAPHY.weight.base};
                    color: ${getColor("text.main")};
                    font-size: ${TYPOGRAPHY.size.small};
                    line-height: ${TYPOGRAPHY.lineHeight.xbase};
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                font-family: ${TYPOGRAPHY.fontFamily.title};
                                font-weight: ${TYPOGRAPHY.weight.base};
                                color: ${getColor("text.main")};
                                font-size: ${TYPOGRAPHY.size.base};
                                line-height: ${TYPOGRAPHY.lineHeight.msmall};
                            `,
                            css`
                                font-family: ${TYPOGRAPHY.fontFamily.title};
                                font-weight: ${TYPOGRAPHY.weight.base};
                                color: ${getColor("text.main")};
                                font-size: ${TYPOGRAPHY.size.base};
                                line-height: ${TYPOGRAPHY.lineHeight.msmall};
                            `,
                            css`
                                font-family: ${TYPOGRAPHY.fontFamily.title};
                                font-weight: ${TYPOGRAPHY.weight.base};
                                color: ${getColor("text.main")};
                                font-size: ${TYPOGRAPHY.size.base};
                                line-height: ${TYPOGRAPHY.lineHeight.msmall};
                            `,
                            null,
                            null,
                        ])}
                }
            `,
            tabContent: css`
                border: none;
                padding: 0px !important;
            `,
        },
    },
    container: {
        css: css`
            padding: 60px 0px 50px 0px;
            > div[class^="TabGroupWrapper"] {
                padding: 0px !important;
            }
        `,
    },
};

const styles = surStaticTabStyles;

const SurShopsOurAdsTab: React.FC<Props> = ({ fields, id }) => {
    return (
        <StyledWrapper {...styles.container}>
            <TabGroup current={fields.numberOfTabs[0]?.fields.name} {...styles.tabGroup}>
                {fields.numberOfTabs.map((numberOfTab, index) => {
                    return (
                        // eslint-disable-next-line react/jsx-key
                        <Tab headline={numberOfTab.fields.name} tabKey={numberOfTab.fields.name} {...styles.tabView}>
                            <Zone contentId={id} zoneName={numberOfTab.fields.name} />
                        </Tab>
                    );
                })}
            </TabGroup>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: SurShopsOurAdsTab,
    definition: {
        group: "Common",
        displayName: "SurShopsOurAdsTab",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.numberOfTabs,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                fieldDefinitions: [
                    {
                        name: "name",
                        displayName: "Tab Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.cssClass,
                displayName: "Add CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};
export default widgetModule;
